// Pastilles COLORÉES des catégories : tag, sévérité, type d'étude, nature d'une
// recommandation. Un seul endroit pour décider de la couleur — sinon chaque
// écran choisit la sienne et la teinte ne veut plus rien dire. Toutes passent
// par la pastille du design system (`Chip`) : ces composants ne font qu'associer
// la valeur métier à une teinte.
import type { InsightProblem, ProblemType, RecoKind } from '../api/types';
import { Chip } from '../components/ui';
import { PRIORITY_LABEL, PROBLEM_TYPE_LABEL, RECO_KIND_LABEL } from '../insights/labels';
import { studyKind } from '../studies/kinds';
import { KIND_STYLE, PRIORITY_HUE, PROBLEM_HUE, RECO_HUE, SEVERITY_HUE, hueOf, statusHue } from './colors';

/** Un tag de la taxonomie : sa teinte se déduit de son libellé. */
export function TagChip({ tag }: { tag: string }) {
  return <Chip hue={hueOf(tag)}>{tag}</Chip>;
}

/** Bloquant, Plafond, Levier. Une valeur inconnue reste neutre. */
export function SeverityChip({ value }: { value: string | null | undefined }) {
  if (!value) return null;
  return <Chip hue={SEVERITY_HUE[value]}>{value}</Chip>;
}

/** Le type d'une étude, avec son pictogramme. Le libellé reste celui saisi. */
export function StudyTypeChip({ type }: { type: string | null | undefined }) {
  if (!type) return null;
  const kind = studyKind(type);
  if (!kind) return <Chip>{type}</Chip>;
  const { hue, icon } = KIND_STYLE[kind];
  return (
    <Chip hue={hue} icon={icon}>
      {type}
    </Chip>
  );
}

export function RecoKindChip({ kind }: { kind: RecoKind | null | undefined }) {
  if (!kind) return null;
  return <Chip hue={RECO_HUE[kind]}>{RECO_KIND_LABEL[kind]}</Chip>;
}

/** Un statut libre (étude, test, projet, traitement d'un insight), coloré par
 *  ce qu'il veut dire. `className` place la pastille (carte du portefeuille). */
export function StatusChip({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return null;
  return (
    <Chip hue={statusHue(value)} className={className}>
      {value}
    </Chip>
  );
}

/** Irritant, besoin, contournement. */
export function ProblemTypeChip({ type }: { type: ProblemType | null | undefined }) {
  if (!type) return null;
  return <Chip hue={PROBLEM_HUE[type]}>{PROBLEM_TYPE_LABEL[type]}</Chip>;
}

/** Le niveau de priorité calculé ; rien quand l'insight n'a pas de preuve. */
export function PriorityChip({ priority }: { priority: InsightProblem['priority'] | undefined }) {
  if (!priority) return null;
  return (
    <Chip hue={PRIORITY_HUE[priority.level]} title={`Score ${String(priority.score).replace('.', ',')}`}>
      {PRIORITY_LABEL[priority.level]}
    </Chip>
  );
}
